import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { debounce } from 'lodash';
import { styled } from '@mui/material/styles';
import { AxiosError } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import CreateTicketProviderModal from './CreateTicketProviderModal';
import DataTable from '../../components/DataTable/index';
import Title from '../../components/Title/index';
import {
  createTicketProviderService,
  deleteTicketProvider,
  getTicketProviders,
  createTicketProviderInterface,
  approveTicketProviderService, 
  rejectTicketProviderService,
  accountTakeoverService
} from '../../services/app/ticket-provider-service';
import { columns } from './table-columns';
import ConfirmationModal from '../../components/ConfirmationModal/index';
import { errorHandler } from '../../utils/network/error-handler';


const PageContent = styled('div')(({ theme }) => ({
  marginBottom: '3rem',
}));

const TicketProvider: React.FC = () => {
  const [openTicketProviderModal, setOpenTicketProviderModal] = useState<boolean>(false);
  const [ticketProviderValues, setTicketProviderValues] = useState<createTicketProviderInterface>({
    name: '',
  });
  const [ticketProviderStatus, setTicketProviderStatus] = useState<string>('');
  const [ticketProviders, setTickerProviders] = useState({
    data: [],
    cursor: {
      afterCursor: '',
      beforeCursor: '',
    },
  });
  const [currentCursor, setCurrentCursor] = useState({
    name: '',
    value: '',
  });
  const [openRejectModal, setOpenRejectModal ] = useState<boolean>(false)
  const [rejectedTicketProviderId, setRejectedTicketProviderId ] = useState<any>(false)
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [deleteTicketProviderId, setDeleteTicketProviderId] = useState('');
  const [tableSize, setTableSize] = useState({
    default: 10,
    list: [5, 10, 25],
  });
  const [searchText, setSearchText] = useState('');
  const [createButtonClicked, setCreateButtonClicked] = useState(false)

  const navigate = useNavigate();
  const location = useLocation();

  const query = useQuery(
    [location.pathname.split('/').includes('validate-backend') ? 'ticket_provider' : 'ticket_providers', tableSize.default, currentCursor.value, searchText, ticketProviderStatus],
    () =>
      getTicketProviders({
        limit: tableSize.default,
        afterCursor: currentCursor.name === 'next' ? currentCursor.value : '',
        beforeCursor: currentCursor.name === 'previuous' ? currentCursor.value : '',
        searchText: searchText,
        location,
        ticketProviderStatus
      }),
    {
      onSuccess: (data) => {
        setTickerProviders(data);
      },
      onError: (err: AxiosError) => errorHandler(err, navigate),
      refetchOnWindowFocus: true,
    },
  );
  const createMutation = useMutation((data: createTicketProviderInterface) => createTicketProviderService(data, location), {
    onSuccess: (data) => {
      query.refetch();
      setTicketProviderValues({ name: '' });
      closeModal();
    },
    onError: (err: AxiosError) => errorHandler(err, navigate),
  });

  const deleteMutation = useMutation((data: string) => deleteTicketProvider(data, location), {
    onSuccess: (data) => {
      query.refetch();
    },
    onError: (err: AxiosError) => errorHandler(err, navigate),
  });

  const approveMutation = useMutation((ticketProviderId: number) => approveTicketProviderService(ticketProviderId), {
    onSuccess: (data) => {
      toast.success('Ticket provider Accepted Successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
      query.refetch();
    },
    onError: (err: AxiosError) => errorHandler(err, navigate),
  })
  const rejectMutation = useMutation((ticketProviderId: number) => rejectTicketProviderService(ticketProviderId), {
    onSuccess: (data) => {
      toast.success('Ticket provider Rejected Successfully', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
      setOpenConfirmationModal(false)
      setOpenRejectModal(false);
      setRejectedTicketProviderId('')
      query.refetch();
    },
    onError: (err: AxiosError) => errorHandler(err, navigate),
  })

  const accountTakeoverMutation = useMutation((ticketProviderId: number) => accountTakeoverService(ticketProviderId), {
    onSuccess: (data) => {
      const { url } = data;

      if(url){
        window.location.replace(url)
      }
    },
    onError: (err: AxiosError) => errorHandler(err, navigate),
  })

  const openModal = () => {
    setOpenTicketProviderModal(true);
  };

  const closeModal = () => {
    setCreateButtonClicked(false)
    setOpenTicketProviderModal(false);
  };

  const createTicketProviderFormValuesHandler = (field: string, value: string) => {
    setTicketProviderValues((prevState) => {
      return {
        ...prevState,
        [field]: value,
      };
    });
  };

  const createTicketProvider = () => {
    setCreateButtonClicked(true)
    if (ticketProviderValues['name'] === '') {
      toast.error('Please Fill all the fields', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
      return;
    }
    createMutation.mutate(ticketProviderValues);
  };

  const openConfirmationModalHandler = (id: string) => {
    setOpenConfirmationModal(true);
    setDeleteTicketProviderId(id);
  };

  const closeConfirmationModalHandler = () => {
    setOpenConfirmationModal(false);
    setDeleteTicketProviderId('');
  };

  const deleteTicketProviderHandler = () => {
    if (deleteTicketProviderId !== '') {
      deleteMutation.mutate(deleteTicketProviderId);
      toast.success('Ticket provider is deleted', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
      setOpenConfirmationModal(false);
    } else {
      toast.error('No ticket Provider is selected', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
    }
  };

  const searchHandler = debounce((value: string) => {
    setSearchText(value);
    query.refetch();
  }, 1000);

  const pageSizeHandler = (pageSize: number) => {
    setTableSize({
      ...tableSize,
      default: pageSize,
    });
    query.refetch();
  };

  const changePageHandler = (changePage: string) => {
    const { cursor } = ticketProviders || {};
    const { afterCursor, beforeCursor } = cursor || {};
    if (changePage === 'go_back' && beforeCursor !== '') {
      setCurrentCursor({
        name: 'previous',
        value: beforeCursor,
      });
    } else {
      if (afterCursor !== '') {
        setCurrentCursor({
          name: 'next',
          value: afterCursor,
        });
      }
    }
    query.refetch();
  };

  const ticketProviderClickHandler = (row: any) => {
    navigate(`/ticket-provider-users/ticket-provider=${row?.id}`)
  }

  const approveTicketProviderHandler = (ticketProviderId: any) => {
    approveMutation.mutate(ticketProviderId);
  }
  
  const accountTakeOverHandler = (ticketProviderId: any) => {
    accountTakeoverMutation.mutate(ticketProviderId);
  }

  const rejectTicketProviderHandler = (ticketProviderId: any) => {
    setOpenConfirmationModal(true)
    setOpenRejectModal(true);
    setRejectedTicketProviderId(ticketProviderId)
  }

  const statusChangeHandler = (field: string, value: string) => {
    setTicketProviderStatus(value)
  }

  const rejectTicketProvider = () => {
    rejectMutation.mutate(rejectedTicketProviderId)
  }

  return (
    <>
      <PageContent>
        <Title title="Ticket Provider" />
      </PageContent>
      <DataTable
        data={ticketProviders?.data.length ? ticketProviders : []}
        deleteHandler={(id: string) => openConfirmationModalHandler(id)}
        columns={columns}
        createClickHandler={openModal}
        buttonText="Create"
        searchHandler={(value) => searchHandler(value)}
        pageSizeChangeHandler={(pageSize: number) => pageSizeHandler(pageSize)}
        tableSize={tableSize}
        changePageHandler={changePageHandler}
        rowClickHandler={ticketProviderClickHandler}
        approveTicketProvider={approveTicketProviderHandler}
        rejectTicketProvider={rejectTicketProviderHandler}
        ticketProviderStatus={['new', 'pending', 'accepted', 'rejected']}
        inputChangeHandler={statusChangeHandler}
        accountTakeOverHandler={accountTakeOverHandler}
      />
      <CreateTicketProviderModal
        title="Create Ticket Provider"
        openModal={openTicketProviderModal}
        closeModal={closeModal}
        submitForm={createTicketProvider}
        inputValueHandler={(field: string, value: string) => createTicketProviderFormValuesHandler(field, value)}
        createButtonClicked={createButtonClicked}
        ticketProviderValues={ticketProviderValues}
      />
      <ConfirmationModal
        title={openRejectModal ? "Reject Ticket Provider" :"Create Ticket Provider"}
        text={openRejectModal ? "Are you sure, You want to reject ticket provider" : "Are you sure, You want to delete ticket provider"}
        openModal={openConfirmationModal}
        closeModal={closeConfirmationModalHandler}
        submitForm={openRejectModal ? rejectTicketProvider : deleteTicketProviderHandler}
      />
      <ToastContainer />
    </>
  );
};

export default TicketProvider;
